import { Card, Stack, styled, useMediaQuery } from '@mui/material';
import { formatCurrency } from '@vestwell-frontend/helpers';
import { Text } from '@vestwell-frontend/ui';

import { FC, ReactNode, useMemo } from 'react';

import { useGiftStateContext } from '../contexts/GiftStateContext';

const StyledStack = styled(Stack)(({ theme }) => ({
  margin: 'auto',
  maxWidth: theme.spacing(180),
  width: '100%' // desired outcome: 720px
}));

const StyledText = styled(Text)(({ theme }) => ({
  marginInline: 'auto',
  maxWidth: theme.spacing(112.5),
  textWrap: 'pretty'
}));

export type GiftFormPageContainerVariant = 'normal' | 'gradient';

const StyledCard = styled(Card, {
  shouldForwardProp: prop =>
    !['customVariant', 'gradientPercentage'].includes(prop.toString())
})<{
  customVariant?: GiftFormPageContainerVariant;
  gradientPercentage?: number;
}>(({ theme, customVariant, gradientPercentage }) => ({
  background:
    customVariant === 'gradient'
      ? `linear-gradient(180deg, ${theme.palette.aliceBlue.main} 0%, ${theme.palette.white.main} ${gradientPercentage}%)`
      : `${theme.palette.white.main}`,
  padding: theme.spacing(6)
}));

export type GiftFormPageContainerProps = {
  children?: ReactNode;
  customHeadlineText?: ReactNode;
  showGiftInformation?: boolean;
  variant?: GiftFormPageContainerVariant;
  gradientPercentage?: number;
};

export const GiftFormPageContainer: FC<GiftFormPageContainerProps> = ({
  showGiftInformation = true,
  variant = 'normal',
  gradientPercentage = 25,
  ...props
}) => {
  const { ctx, giftAndAccount } = useGiftStateContext();

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const headerTextVariant = useMemo(
    () => (isSmallScreen ? 'e2' : 'c2'),
    [isSmallScreen]
  );

  const subHeaderTextVariant = useMemo(
    () => (isSmallScreen ? 'j1' : 'g1'),
    [isSmallScreen]
  );

  return (
    <StyledStack role='main'>
      {ctx?.step?.caption && (
        <Text textAlign='center' variant={subHeaderTextVariant}>
          {ctx?.step?.caption} | {ctx?.step?.title}
        </Text>
      )}
      <StyledCard
        customVariant={variant}
        gradientPercentage={gradientPercentage}>
        {props.customHeadlineText}
        {showGiftInformation && (
          <Text mb={1} mt={2} textAlign='center' variant='i1'>
            Give a {formatCurrency(ctx.state?.giftSubmission?.amount)}
            {ctx.state?.giftSubmission?.isRecurring
              ? ' recurring '
              : ' one-time '}
            gift to {giftAndAccount.data?.data?.accountPublic?.firstName}
          </Text>
        )}
        {ctx?.step?.subTitle && (
          <StyledText
            aria-level={1}
            role='heading'
            textAlign='center'
            variant={headerTextVariant}>
            {ctx?.step?.subTitle}
          </StyledText>
        )}

        {props.children}
      </StyledCard>
    </StyledStack>
  );
};
